/**
 * What this client can host, evaluated when asked rather than at import.
 *
 * The capability that matters is narrow: can this client park an OS-level
 * browser view over a DOM slot? That is true exactly when the desktop preload
 * has exposed the native browser bridge, which is the same object
 * `DesktopNativeBrowserSurface` requires to attach one.
 *
 * Deliberately not `isElectron` from `~/env`. That is a module-level const
 * evaluated at import time, so anything that installs the bridge after the
 * bundle evaluates reads as a web client forever -- including every browser-mode
 * test of the desktop path, which installs the bridge in its own setup. Reading
 * the bridge itself, per call, also removes the second source of truth: the
 * surface checks for the bridge, so the decision to mount it should check for
 * the same thing rather than a proxy for it.
 *
 * @module BrowserPreviewPanel.clientCapability
 */
import type { BrowserClientCapability } from "@cmmd-forge/shared/browserPresentation";

export function browserClientCapability(): BrowserClientCapability {
  if (typeof window === "undefined") {
    return "web";
  }
  return window.desktopBridge?.nativeBrowser ? "native-host" : "web";
}
